<template>
  <div class="offline-letter">
    <el-descriptions :column="3" size="medium">
      <el-descriptions-item label="保函状态">
        <status-tag
          v-if="letterStatusEntry.label"
          :label="letterStatusEntry.label"
          :type="letterStatusEntry.type"
          size="small"
        />
        <status-tag v-else type="danger" label="未开立" size="small" />
      </el-descriptions-item>
      <el-descriptions-item label="保函有效期要求">
        <span>{{ gfLetterDays || '-' }} 天</span>
      </el-descriptions-item>
    </el-descriptions>

    <!-- 审核拒绝原因 -->
    <div
      v-if="
        letter.approvalStatus ===
          guaranteeLetterOfflineStatusDict.AUDIT_FAIL.value
      "
      class="opinion m-b-16"
    >
      不通过原因：{{ letter.gfLetterApprovalOp || '-' }}
    </div>

    <el-descriptions :column="3" size="medium">
      <el-descriptions-item label="保函文件">
        <upload
          key="reUploadFile"
          style="width: 220px"
          :file-id-before="letter.gfLetterFileId"
          :props-data="{
            uploadMode: 'fileId',
            fileSize: 1,
            beforeUpload: beforeUpload,
            multiple: false,
            fileListHiddenControl: {
              hiddenFileList: false,
              hiddenUploadBtn: !showUploadButton
            },
            onSuccess: handleReUploadSuccess
          }"
        >
          <template v-slot:btn>
            <a-button style="padding: 0" type="link">
              {{ isInitStatus ? '线下保函上传' : '重新上传线下保函' }}
            </a-button>
          </template>
          <template v-slot:tip>
            <span></span>
          </template>
        </upload>
      </el-descriptions-item>
      <el-descriptions-item label="提交时间">
        <span>{{ letter.gfLetterApplyTime || '-' }}</span>
      </el-descriptions-item>
    </el-descriptions>

    <!-- 提交操作区 -->
    <template v-if="showSubmit">
      <el-divider />
      <div class="flex col-center row-center">
        <ol class="tip">
          <li>线上保函申请由江咨金发线上服务平台提供；</li>
          <li>
            线下保函上传支持pdf/png/jpg/zip/rar格式，单个文件最大50MB，最多上传1个。
          </li>
        </ol>

        <a-button
          type="primary"
          style="width: 120px; height: 32px"
          @click="handleSubmit"
        >
          提交审核
        </a-button>
      </div>
    </template>
  </div>
</template>

<script>
// 组件
import Upload from '@/components/common/Upload'
import StatusTag from '@/components/common/StatusTag'
// 字典
import {
  guaranteeLetterOfflineStatusDict,
  guaranteePayMethodDict
} from '@/dictionary/project/common'
import {
  getGuaranteeLetterDetailApi,
  setGuaranteePayMethodApi,
  submitGuaranteeOfflineLetterApi
} from '@/api/supplier/bid'
import { cloneDeep } from 'lodash'
import { bidInviteBidStatusDict } from '@/dictionary/project/bid'
import { isBefore } from 'date-fns'
import { mapState } from 'vuex'

/**
 * @description 线下保函操作组件
 */
export default {
  name: 'OfflineLetter',

  components: { Upload, StatusTag },

  props: {
    orderId: {
      type: [String, Number],
      required: true
    },

    tradeId: {
      type: [String, Number],
      required: true
    },

    // 项目状态
    orderStatus: {
      type: [String, Number],
      default: ''
    },

    // 允许更换线下保函的时间节点
    canChangeFileTimeNode: {
      type: String,
      default: ''
    },

    // 保函有效期要求
    gfLetterDays: {
      type: [Number, String],
      default: ''
    }
  },

  data() {
    return {
      guaranteeLetterOfflineStatusDict,

      letter: {
        // 保函状态
        approvalStatus: '',
        // 提交时间
        createTime: '',
        // 保函文件
        gfLetterFileId: ''
      },
      // 线下保函文件是否修改
      fileChanged: false
    }
  },

  computed: {
    ...mapState('systemTime', ['systemMillisecond']),

    isInitStatus() {
      // 未选择保证金支付方式时，letter对象为空
      const IS_NULL =
        this.letter.approvalStatus == undefined ||
        this.letter.approvalStatus == ''
      // 选择线上保函退款后，切换至线下保函时，approvalStatus为初始状态
      const IS_INIT =
        this.letter.approvalStatus ===
        guaranteeLetterOfflineStatusDict.INIT.value
      return IS_NULL || IS_INIT
    },

    letterStatusEntry() {
      return (
        guaranteeLetterOfflineStatusDict.translate(
          this.letter.approvalStatus
        ) || {}
      )
    },

    // 显示线下保函上传按钮
    showOfflineLetterUpload() {
      // 当保函状态不存在或审核不通过时显示
      return (
        !this.letter.approvalStatus ||
        this.letter.approvalStatus ===
          guaranteeLetterOfflineStatusDict.AUDIT_FAIL.value
      )
    },

    // 显示上传线下保函按钮
    showUploadButton() {
      const IS_BEFORE = isBefore(
        new Date(this.systemMillisecond),
        new Date(this.canChangeFileTimeNode)
      )
      // 开标之后不允许上传，审核中不允许上传
      if (
        this.letter.approvalStatus ==
          guaranteeLetterOfflineStatusDict.INIT.value ||
        this.letter.approvalStatus ==
          guaranteeLetterOfflineStatusDict.AUDIT_FAIL.value ||
        this.letter.approvalStatus ==
          guaranteeLetterOfflineStatusDict.AUDIT_SUCCESS.value
      ) {
        return IS_BEFORE
      }
      return false
    },

    // 显示提交审核按钮
    showSubmit() {
      /**
       * 当为初始状态，或审核状态不通过时显示，审核通过且线下保函文件已修改时也显示
       */
      // 审核不通过
      const auditFail =
        this.letter.approvalStatus ===
        guaranteeLetterOfflineStatusDict.AUDIT_FAIL.value
      // 审核通过
      const auditSuccess =
        this.letter.approvalStatus ===
        guaranteeLetterOfflineStatusDict.AUDIT_SUCCESS.value
      return (
        this.isInitStatus || auditFail || (auditSuccess && this.fileChanged)
      )
    }
  },

  created() {
    this.$store.dispatch('systemTime/getSystemTime')
    this.getLetterDetail()
  },

  methods: {
    /**
     * 上传文件校验
     * @param file
     */
    beforeUpload(file) {
      const _type = file.name.split('.').pop()
      const fileTypes = ['pdf', 'png', 'jpg', 'zip', 'rar']
      if (!fileTypes.includes(_type.toLowerCase())) {
        this.$message.error(`不支持.${_type}格式的文件上传`)
        return false
      }

      const isLt50M = file.size / 1024 / 1024 <= 50
      if (!isLt50M) {
        this.$message.error('上传文件大小不能超过 50MB!')
        return false
      }

      return true
    },

    /**
     * 查询保函结果
     */
    getLetterDetail() {
      const params = {
        orderId: this.orderId,
        tradeId: this.tradeId
      }
      getGuaranteeLetterDetailApi(params)
        .then(res => {
          if (res.data) {
            this.letter = res.data
            this.letter.gfLetterFileId =
              res.data?.gfLetterFileIds?.length > 0
                ? res.data?.gfLetterFileIds[0] || ''
                : ''
            this.$emit('getLetter', this.letter)
          }
        })
        .catch(err => {
          console.error('查询保函结果失败', err)
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 提交审核
     */
    handleSubmit() {
      // 校验线下保函附件是否上传
      if (!this.letter.gfLetterFileId) {
        this.$message.warning('请先上传线下保函文件')
        return
      }

      setGuaranteePayMethodApi(this.orderId, this.tradeId, {
        gfPayType: guaranteePayMethodDict.OFFLINE_LETTER.value
      })
        .then(res => {
          const postData = {
            orderId: this.orderId,
            tradeId: this.tradeId,
            gfLetterFileIds: [this.letter.gfLetterFileId]
          }
          submitGuaranteeOfflineLetterApi(postData)
            .then(res => {
              this.getLetterDetail()
              this.$emit('refresh')
            })
            .catch(err => {
              console.error('提交线下保函审核失败', err)
              if (err?.message) this.$message.error(err.message)
            })
        })
        .catch(err => {
          console.error('设置保证金缴费方式失败', err)
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 重新上传线下保函附件
     */
    handleReUploadSuccess(fileId) {
      this.letter.gfLetterFileId = fileId
      this.fileChanged = true
      console.log('重新上传线下保函文件', this.letter, fileId)
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-descriptions {
  .el-descriptions-item {
    vertical-align: top !important;
    padding-bottom: 16px !important;
  }
}

.opinion {
  background-color: $danger-fourth-color;
  border: 1px solid $danger-second-color;
  color: $text-color;
  font-size: 12px;
  @include text-bold;
  border-radius: 4px;
  padding: 8px 14px;
}

::v-deep .upload-wrapper .upload-btn {
  margin: 0;
  vertical-align: middle;
}

ol.tip,
.tip {
  font-size: 12px;
  color: $third-text-color;
}

ol.tip {
  margin-bottom: 0;
  padding-left: 12px;
  line-height: 2;
  position: absolute;
  left: 24px;
}
</style>
